import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { postFeatureConfiguration } from "../../../store/actions";
import {
  Modal,
  Button,
  MenuItem,
  FormControl,
  Select,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { makeStyles } from '@mui/styles';
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import BucketPolicyModal from "./BucketPolicyModal";
import spinner from "../../../assets/images/loadingGif.gif";
import { enqueueSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    overflowY: "auto",
    maxHeight: "100vh",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
}));

const AdditionalAddonsModal = (props) => {
  const classes = useStyles();
  const curEstimatesNumDays = props?.addons?.find(
    (addOn) => addOn.name === "Cost Insight"
  )?.curEstimatesNumDays;
  const [costInsightValue, setCostInsightValue] = useState(
    curEstimatesNumDays || 1
  ); // State for dropdown
  const [isCostInsightEnabled, setIsCostInsightEnabled] = useState(false); // Track if Cost Insight is enabled

  // State for lock/unlock status fields (false = locked, true = unlocked)
  const [costInsightStatus, setCostInsightStatus] = useState(
    props.account?.costInsightStatus !== undefined ? props.account.costInsightStatus : true
  );
  const [costOptimizationStatus, setCostOptimizationStatus] = useState(
    props.account?.costOptimizationStatus !== undefined ? props.account.costOptimizationStatus : true
  );
  const [eventInsightStatus, setEventInsightStatus] = useState(
    props.account?.eventInsightStatus !== undefined ? props.account.eventInsightStatus : true
  );

  const addons = useMemo(() => props.addons || [], [props.addons]);
  
  // State to track enabled status for each addon
  const [addonEnabledStates, setAddonEnabledStates] = useState({});
  
  // Store initial values to detect changes
  const [initialValues, setInitialValues] = useState({
    enabledStates: {},
    costInsightValue: null,
    costInsightStatus: null,
    costOptimizationStatus: null,
    eventInsightStatus: null,
  });
  
  // Initialize enabled states from addons and store initial values
  useEffect(() => {
    const initialStates = {};
    addons.forEach((addon) => {
      initialStates[addon.name] = addon.enabled || false;
    });
    setAddonEnabledStates(initialStates);
    
    // Store initial values for comparison
    setInitialValues({
      enabledStates: { ...initialStates },
      costInsightValue: curEstimatesNumDays || 1,
      costInsightStatus: props.account?.costInsightStatus !== undefined ? props.account.costInsightStatus : true,
      costOptimizationStatus: props.account?.costOptimizationStatus !== undefined ? props.account.costOptimizationStatus : true,
      eventInsightStatus: props.account?.eventInsightStatus !== undefined ? props.account.eventInsightStatus : true,
    });
  }, [addons, props.account]);

  useEffect(() => {
    // Check if Cost Insight add-on is enabled
    const costInsightAddon = addons.find(
      (addon) => addon.name === "Cost Insight"
    );
    if (costInsightAddon) {
      setIsCostInsightEnabled(costInsightAddon.enabled);
    }
  }, [addons]);

  useEffect(() => {
    // Initialize status fields from account props (false = locked, true = unlocked)
    if (props.account) {
      setCostInsightStatus(props.account.costInsightStatus !== undefined ? props.account.costInsightStatus : true);
      setCostOptimizationStatus(props.account.costOptimizationStatus !== undefined ? props.account.costOptimizationStatus : true);
      setEventInsightStatus(props.account.eventInsightStatus !== undefined ? props.account.eventInsightStatus : true);
    }
  }, [props.account]);

  const handleCostInsightChange = (event) => {
    event.stopPropagation();
    setCostInsightValue(event.target.value);
  };

  // State for Bucket Policy Modal
  const [bucketPolicyModalOpen, setBucketPolicyModalOpen] = useState(false);
  const [pendingAddonEnable, setPendingAddonEnable] = useState(null); // Track which addon is being enabled
  const [showError, setShowError] = useState(false); // Track if error should be displayed

  const handleAddonEnabledChange = (addonName) => (event) => {
    event.stopPropagation();
    const isEnabled = event.target.checked;
    
    // If enabling Cost Insight or Event Insight, show bucket policy modal first
    if (isEnabled && (addonName === "Cost Insight" || addonName === "Event Insight")) {
      setPendingAddonEnable(addonName);
      setBucketPolicyModalOpen(true);
      // Don't update state yet - wait for bucket policy modal success
    } else if (isEnabled && addonName === "Cost Optimization") {
      // For Cost Optimization, call POST API directly without opening modal
      const addon = props.account?.subscription?.addons?.find(
        (addon) => addon && addon.name === "Cost Optimization"
      );
      
      if (addon && props.account) {
        // Optimistically update state to show toggle as enabled immediately
        setAddonEnabledStates((prevStates) => ({
          ...prevStates,
          [addonName]: isEnabled,
        }));
        
        const payload = {
          role: addon.replicationRole && addon.replicationRole !== "NA" ? addon.replicationRole : "",
          accountNumber: props.account.accountNumber || "",
          bucketName: addon.bucketName && addon.bucketName !== "NA" ? addon.bucketName : "",
          readOnlyRole: props.account.iamrole || "",
          selectedRegion: addon.region && addon.region !== "NA" ? addon.region : "",
          module: "Cost Optimization",
          externalId: props.account.externalid || props.account.externalId || "",
        };
        
        setPendingAddonEnable(addonName);
        props.postFeatureConfiguration(payload);
      } else {
        // If no addon data, just update state
        setAddonEnabledStates((prevStates) => ({
          ...prevStates,
          [addonName]: isEnabled,
        }));
      }
    } else {
      // For disabling, update state directly
      setAddonEnabledStates({
        ...addonEnabledStates,
        [addonName]: isEnabled,
      });
    }
  };

  const handleModalClose = useCallback(() => props.setOpen(false), [props.setOpen]);

  // Handle feature configuration API success (for both modal and direct calls)
  useEffect(() => {
    if (props.postFeatureConfigurationMessage && pendingAddonEnable) {
      // Feature configuration submitted successfully, now enable the addon
      setAddonEnabledStates((prevStates) => ({
        ...prevStates,
        [pendingAddonEnable]: true,
      }));
      setBucketPolicyModalOpen(false);
      setPendingAddonEnable(null);
      // Show success snackbar
      enqueueSnackbar(props.postFeatureConfigurationMessage, { variant: "success" });
      // Don't close AdditionalAddonsModal - allow user to make other changes and submit together
    }
  }, [props.postFeatureConfigurationMessage, pendingAddonEnable]);

  // Handle feature configuration API error - only for Cost Optimization
  // Cost Insight and Event Insight errors are handled in BucketPolicyModal
  useEffect(() => {
    if (props.postFeatureConfigurationError && pendingAddonEnable === "Cost Optimization") {
      // If API call failed, reset the toggle state back to disabled
      setAddonEnabledStates((prevStates) => ({
        ...prevStates,
        [pendingAddonEnable]: false,
      }));
      setPendingAddonEnable(null);
      setBucketPolicyModalOpen(false);
      setShowError(true); // Show error alert
    }
  }, [props.postFeatureConfigurationError, pendingAddonEnable]);

  const handleBucketPolicyModalClose = () => {
    setBucketPolicyModalOpen(false);
    setPendingAddonEnable(null);
  };

  const isAddonLocked = (addonName) => {
    if (addonName === "Cost Insight") {
      return !costInsightStatus; // false means locked
    } else if (addonName === "Cost Optimization") {
      return !costOptimizationStatus;
    } else if (addonName === "Event Insight") {
      return !eventInsightStatus;
    }
    return false; // Other addons are not lockable
  };

  // Check if there are any changes made by the user
  const hasChanges = () => {
    // Check if enabled states changed
    const enabledChanged = Object.keys(addonEnabledStates).some(
      (addonName) => addonEnabledStates[addonName] !== initialValues.enabledStates[addonName]
    );

    // Check if cost insight value changed
    const costInsightValueChanged = costInsightValue !== initialValues.costInsightValue;

    // Check if lock statuses changed
    const costInsightStatusChanged = costInsightStatus !== initialValues.costInsightStatus;
    const costOptimizationStatusChanged = costOptimizationStatus !== initialValues.costOptimizationStatus;
    const eventInsightStatusChanged = eventInsightStatus !== initialValues.eventInsightStatus;

    return (
      enabledChanged ||
      costInsightValueChanged ||
      costInsightStatusChanged ||
      costOptimizationStatusChanged ||
      eventInsightStatusChanged
    );
  };

  const handleStatusChange = (addonName) => (event) => {
    event.stopPropagation();
    const isLocked = event.target.checked; // true = locked (toggle ON), false = unlocked (toggle OFF)
    // In payload: false = locked, true = unlocked
    // So if toggle is ON (checked/true), send false (locked)
    // If toggle is OFF (unchecked/false), send true (unlocked)
    const payloadValue = !isLocked;
    if (addonName === "Cost Insight") {
      setCostInsightStatus(payloadValue);
      // When locking, disable the addon (set enabled to false)
      if (isLocked) {
        setAddonEnabledStates({
          ...addonEnabledStates,
          [addonName]: false,
        });
      }
    } else if (addonName === "Cost Optimization") {
      setCostOptimizationStatus(payloadValue);
      // When locking, disable the addon (set enabled to false)
      if (isLocked) {
        setAddonEnabledStates({
          ...addonEnabledStates,
          [addonName]: false,
        });
      }
    } else if (addonName === "Event Insight") {
      setEventInsightStatus(payloadValue);
      // When locking, disable the addon (set enabled to false)
      if (isLocked) {
        setAddonEnabledStates({
          ...addonEnabledStates,
          [addonName]: false,
        });
      }
    }
  };

  const getStatusForAddon = (addonName) => {
    // Returns true if locked (for toggle checked state)
    // In state: false = locked, true = unlocked
    // For toggle: checked (ON) = locked, unchecked (OFF) = unlocked
    // So we need to invert: if status is false (locked), return true (checked)
    if (addonName === "Cost Insight") {
      return !costInsightStatus; // If false (locked), return true (checked)
    } else if (addonName === "Cost Optimization") {
      return !costOptimizationStatus;
    } else if (addonName === "Event Insight") {
      return !eventInsightStatus;
    }
    return false; // default to unlocked (toggle OFF)
  };

  let updateOrgAccountMsg = props.updateOrgAccountMessage;
  useEffect(() => {
    if (updateOrgAccountMsg) {
      handleModalClose();
    }
  }, [updateOrgAccountMsg, handleModalClose]);

  const handleUpdate = () => {
    console.log("Update Clicked with Cost Insight value:", costInsightValue);

    const complianceAddOns = props?.complianceAddOns?.map((addOn) => ({
      name: addOn.name,
      enabled: addOn.enabled,
    }));

    const addOns = props?.addons?.map((addOn) => {
      let enabledValue = addonEnabledStates[addOn.name] !== undefined 
        ? addonEnabledStates[addOn.name] 
        : addOn?.enabled;
      
      // If addon is locked, set enabled to false
      if (addOn.name === "Cost Insight" && !costInsightStatus) {
        // costInsightStatus is false when locked
        enabledValue = false;
      } else if (addOn.name === "Cost Optimization" && !costOptimizationStatus) {
        // costOptimizationStatus is false when locked
        enabledValue = false;
      } else if (addOn.name === "Event Insight" && !eventInsightStatus) {
        // eventInsightStatus is false when locked
        enabledValue = false;
      }
      // If unlocked, use the state value (from toggle)

      if (addOn.name === "Cost Insight") {
        return {
          name: addOn?.name,
          enabled: enabledValue,
          curEstimatesNumDays: costInsightValue,
        };
      } else {
        return {
          name: addOn?.name,
          enabled: enabledValue,
          curEstimatesNumDays: addOn?.curEstimatesNumDays,
        };
      }
    });

    const updateOrgAccountPayload = {
      acccode: props?.orgAccountCode,
      subscription: props?.subscription,
      expiration_date: props?.expiration_date,
      complianceAddOns,
      addOns,
      subscription_type: props?.subscription_type,
      resourceGroupRequestDTOS: props.resourceGroup,
      marketplaceDetailsDTO: {
        offerId: props.account?.marketplaceDetails?.offerId || "",
        agreementId: props.account?.marketplaceDetails?.agreementId || ""
      },
      scan_frequency: ""+ props.account?.scan_frequency,
      contract_amount: props.account?.contract_amount,
      threshold_amount: props.account?.threshold_amount,
      usage_percentage: props.account?.usage_percentage,
      islocked: props.account?.islocked || false,
      payment_term: props.account?.payment_term || "N/A",
      wafr_workload_limit: props.account?.wafr_workload_limit || "",
      costInsightStatus: costInsightStatus,
      costOptimizationStatus: costOptimizationStatus,
      eventInsightStatus: eventInsightStatus,
    };

    console.log("updateOrgAccountPayload : ", updateOrgAccountPayload);
    props.updateOrgAccount(updateOrgAccountPayload);
  };

  const columns = [
    { field: "name", headerName: "AddOns", width: 150 },
    {
      field: "options",
      headerName: "Options",
      width: 180,
      renderCell: (params) => {
        const addonName = params.row.name;
        const isUnlocked = getStatusForAddon(addonName);
        
        const isLocked = getStatusForAddon(addonName); // true = locked (toggle ON), false = unlocked (toggle OFF)
        
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {addonName === "Cost Insight" && isCostInsightEnabled && (
              <>
                <FormControl className={classes.formControl} size="small">
                  <Select
                    value={costInsightValue}
                    onChange={handleCostInsightChange}
                    disabled={!isCostInsightEnabled}
                    fullWidth
                  >
                    {[...Array(6).keys()].map((i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="The number of days to capture estimates in cost insight">
                  <IconButton size="small">
                    <InfoIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {(addonName === "Cost Insight" || 
              addonName === "Cost Optimization" || 
              addonName === "Event Insight") && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isLocked}
                    onChange={handleStatusChange(addonName)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {isLocked ? (
                      <>
                        <LockIcon fontSize="small" style={{ color: "#f44336" }} />
                        <span style={{ color: "#f44336", fontSize: "0.875rem" }}>Locked</span>
                      </>
                    ) : (
                      <>
                        <LockOpenIcon fontSize="small" style={{ color: "#4caf50" }} />
                        <span style={{ color: "#4caf50", fontSize: "0.875rem" }}>Unlocked</span>
                      </>
                    )}
                  </div>
                }
                labelPlacement="end"
              />
            )}
          </div>
        );
      },
    },
    { 
      field: "description", 
      headerName: "Description", 
      width: 200,
      hide: true,
    },
    {
      field: "enabled",
      headerName: "Status",
      width: 180,
      renderCell: (params) => {
        const addonName = params.row.name;
        const isLocked = isAddonLocked(addonName);
        const isEnabled = addonEnabledStates[addonName] !== undefined 
          ? addonEnabledStates[addonName] 
          : params.row.enabled;
        
        const toggleSwitch = (
          <Switch
            checked={isEnabled}
            onChange={handleAddonEnabledChange(addonName)}
            disabled={isLocked}
            color="primary"
            size="small"
          />
        );

        if (isLocked) {
          return (
            <Tooltip title="Please unlock this addon and then activate">
              <span>
                <FormControlLabel
                  control={toggleSwitch}
                  label={
                    <span style={{ 
                      fontSize: "0.875rem",
                      color: isEnabled ? "#193A6F" : "red",
                      fontWeight: "bold"
                    }}>
                      {isEnabled ? "Active" : "Inactive"}
                    </span>
                  }
                  labelPlacement="end"
                />
              </span>
            </Tooltip>
          );
        }

        return (
          <FormControlLabel
            control={toggleSwitch}
            label={
              <span style={{ 
                fontSize: "0.875rem",
                color: isEnabled ? "#193A6F" : "red",
                fontWeight: "bold"
              }}>
                {isEnabled ? "Active" : "Inactive"}
              </span>
            }
            labelPlacement="end"
          />
        );
      },
    },
    {
      field: "price",
      headerName: "Price",
      width: 100,
      hide: true,
      renderCell: (params) => "$" + params.row.price,
    },
  ];

  if (!props.isOpen) {
    return null;
  }

  return (
    <>
      <Modal
        className={classes.modal}
        open={true}
        onClose={handleModalClose}
      >
        <div className={classes.paper}>
          <h3>List of AddOns for Organization Account</h3>
          {(props.isUpdatingOrgAccount || props.isPostingFeatureConfiguration) && (
            <div
              style={{
                display: "flex",
                direction: "column",
                alignContent: "center",
                alignItems: "center",
                justifyContent: "center",
                justifyItems: "center",
                marginBottom: "16px",
              }}
            >
              <img src={spinner} width="32px" alt="Spinner" />
            </div>
          )}
          {props.postFeatureConfigurationError && showError && (
            <Alert 
              severity="error" 
              onClose={() => setShowError(false)} 
              sx={{ marginBottom: "16px" }}
            >
              <AlertTitle>Error</AlertTitle>
              {typeof props.postFeatureConfigurationError === 'string' 
                ? props.postFeatureConfigurationError 
                : props.postFeatureConfigurationError?.message || 'An error occurred while configuring the feature. Please try again.'}
            </Alert>
          )}
          <DataGrid
            rows={addons}
            columns={columns}
            pageSize={5}
            components={{
              Toolbar: GridToolbar,
            }}
            disableSelectionOnClick
            autoHeight
            disableColumnMenu
            disableColumnSelector
            getRowId={(row) => row.name}
          />

          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdate}
              disabled={!hasChanges() || props.isUpdatingOrgAccount || props.isPostingFeatureConfiguration}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bucket Policy Modal - rendered outside the AdditionalAddonsModal */}
      {bucketPolicyModalOpen && pendingAddonEnable && (
        <BucketPolicyModal
          isOpen={bucketPolicyModalOpen}
          closeModal={handleBucketPolicyModalClose}
          account={props.account}
          addOnsName={pendingAddonEnable}
          allAccounts={props.allAccounts || []}
          setNotificationSent={props.setNotificationSent}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    postFeatureConfigurationMessage,
    postFeatureConfigurationError,
    isPostingFeatureConfiguration,
    isUpdatingOrgAccount,
  } = state.orgAccount;

  return {
    postFeatureConfigurationMessage,
    postFeatureConfigurationError,
    isPostingFeatureConfiguration,
    isUpdatingOrgAccount,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    postFeatureConfiguration: (payload) => {
      dispatch(postFeatureConfiguration(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalAddonsModal);
